import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { colors, fonts } from '../brand';
import Disclaimer from '../components/Disclaimer';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

export default function Home() {
  const [feedback, setFeedback] = useState({ message: '', name: '', email: '' });
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const handleFeedback = async (e) => {
    e.preventDefault();
    setFeedbackLoading(true);
    try {
      await axios.post(`${API}/api/content/feedback`, feedback);
      setFeedbackSent(true);
    } catch {}
    setFeedbackLoading(false);
  };

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Elephants Answering — Divorce Support for Women</title>
        <meta name="description" content="A community of women who've lived it. Real stories, plain-language information, and tools to help you navigate divorce — wherever you are in the process." />
              <link rel="canonical" href="https://elephantsanswering.com/" />
    </Helmet>
      <SiteNav />

      <main>
      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <p style={styles.heroEyebrow}>DURING ONE OF LIFE'S HARDEST SEASONS</p>
          <h1 style={styles.heroTitle}>You're In<br />The Right place.</h1>
          <p style={styles.heroQuote}>
            Elephants Answering is a community of women who've lived through or are living through a divorce — we know the pit-in-your-stomach feeling. Together, we come out stronger, more prepared, and ready for a brilliant future.
          </p>
          <p style={styles.heroTaglineSub}>— For Women By Women —</p>
          <p style={styles.heroSubtitle}>
            Elephants live in matriarchal societies. When one is in distress, they call to warn each other of danger to protect the herd. Elephants Answering honors this instinct.
          </p>
          <div style={styles.freeResources}>
            <div style={styles.freeBtns}>
              <Link to="/listen" style={styles.freeLinkLarge}>Listen</Link>
              <Link to="/qa" style={styles.freeLinkLarge}>Q&amp;A</Link>
              <Link to="/refuge" style={styles.freeLinkLarge}>Refuge</Link>
              <Link to="/resources" style={styles.freeLinkLarge}>Resources</Link>
              <Link to="/quiz" style={styles.freeLinkLarge}>Roadmap</Link>
            </div>
          </div>
          <Link to="/register" style={styles.heroCta}>
            Join the Community
          </Link>
          <ul style={styles.heroChecklist}>
            {['Hear More Stories', 'Explore Our Answers', 'Free for Early Members'].map(item => (
              <li key={item} style={styles.heroCheckItem}>
                <span style={styles.checkMark}>✓</span> {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Features */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Share Your Experience</h2>
        <p style={styles.sectionSubtitle}>Another Woman Needs Exactly What You Know</p>
        <div style={styles.features}>
          <Link to="/listen" style={styles.featureLink}>
            <div style={styles.featureAccent}>✦</div>
            <h3 style={styles.featureTitle}>Send a Voice Memo</h3>
            <p style={styles.featureDesc}>Share your story, a moment, or a lesson learned — so women starting out or in the middle of it feel less alone and more prepared.</p>
          </Link>
          <a href="#join" style={styles.featureLink}>
            <div style={styles.featureAccent}>✦</div>
            <h3 style={styles.featureTitle}>Submit Your Q&amp;A</h3>
            <p style={styles.featureDesc}>Share a question, something you wish you'd known, or an answer you found a little too late — so women starting out or in the middle of it feel less in the dark and more prepared.</p>
          </a>
          <Link to="/refuge" style={styles.featureLink}>
            <div style={styles.featureAccent}>✦</div>
            <h3 style={styles.featureTitle}>Refuge — Let Us Know</h3>
            <span style={styles.featureTag}>Coming Soon</span>
            <p style={styles.featureDesc}>Would you want a place to pause, land, breathe? Imagine somewhere with other women going through it — where hotel meets home, small, supportive, flexible coliving. Let us know if you'd want this, so women starting out or in the middle of it have somewhere to land.</p>
          </Link>
        </div>
      </section>

      {/* Founder story */}
      <section style={styles.founderSection}>
        <div style={styles.founderInner}>
          <h2 style={{...styles.sectionTitle, color: colors.white, marginBottom: '28px'}}>Built by someone who's been there.</h2>
          <p style={styles.founderText}>
            Our founder, Amy, knows firsthand what it's like to navigate a high-conflict, complex divorce involving children. She didn't know many women who had been through it—but she needed to. She wanted real answers, in plain language.
          </p>
          <p style={styles.founderText}>
            There wasn't a place to connect her experience to others—so she built one. Our mission isn't theoretical—it's personal.
          </p>
          <Link to="/about" style={styles.founderLink}>Learn More</Link>
        </div>
      </section>

      {/* CTA */}
      <section id="join" style={styles.ctaSection}>
        <Link to="/register" style={styles.ctaLink}>
          Join the Community
        </Link>
        <p style={styles.ctaTagline}>Free for Early Members</p>
      </section>

      {/* Feedback */}
      <section style={styles.feedbackSection}>
        <div style={styles.feedbackInner}>
          <p style={styles.feedbackEyebrow}>WE'RE NEW HERE</p>
          <h2 style={styles.feedbackTitle}>What Else Would Help?</h2>
          <p style={styles.feedbackSubtitle}>Tell Us What You Need to Help Shape What We Build.</p>
          {feedbackSent ? (
            <p style={styles.feedbackThanks}>Thank you — we read every note.</p>
          ) : (
            <form onSubmit={handleFeedback} style={styles.feedbackForm}>
              <textarea
                style={styles.feedbackTextarea}
                placeholder="Your feedback..."
                rows={4}
                value={feedback.message}
                onChange={e => setFeedback({ ...feedback, message: e.target.value })}
                required
              />
              <div style={styles.feedbackRow}>
                <input style={styles.feedbackInput} placeholder="Name (optional)" value={feedback.name} onChange={e => setFeedback({ ...feedback, name: e.target.value })} />
                <input style={styles.feedbackInput} placeholder="Email (optional)" type="email" value={feedback.email} onChange={e => setFeedback({ ...feedback, email: e.target.value })} />
              </div>
              <button style={styles.feedbackBtn} type="submit" disabled={feedbackLoading}>
                {feedbackLoading ? 'Sending...' : 'Send Feedback'}
              </button>
            </form>
          )}
        </div>
      </section>

      </main>
      <Disclaimer />
      <SiteFooter />
    </div>
  );
}

const styles = {
  page: { fontFamily: fonts.body, backgroundColor: colors.white, minHeight: '100vh' },

  hero: { background: `linear-gradient(150deg, ${colors.navy} 0%, #003366 100%)`, padding: '110px 20px' },
  heroInner: { maxWidth: '680px', margin: '0 auto', textAlign: 'center' },
  heroEyebrow: { fontFamily: fonts.body, color: colors.peach, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '20px' },
  heroTitle: { fontFamily: fonts.heading, color: colors.white, fontSize: '44px', lineHeight: '1.35', marginBottom: '16px', fontWeight: '600', letterSpacing: '0.5px' },
  heroTagline: { fontFamily: fonts.heading, color: colors.white, fontSize: '28px', lineHeight: '1.35', marginBottom: '8px', fontWeight: '600', letterSpacing: '0.5px', textAlign: 'center' },
  heroTaglineSub: { fontFamily: fonts.heading, color: colors.white, fontSize: '28px', lineHeight: '1.35', marginBottom: '24px', fontWeight: '600', letterSpacing: '0.5px', textAlign: 'center' },
  heroQuote: { fontFamily: fonts.heading, color: colors.peach, fontSize: '14px', lineHeight: '2', marginBottom: '36px', fontStyle: 'italic', fontWeight: '400', borderLeft: `2px solid ${colors.rose}`, paddingLeft: '20px', textAlign: 'left' },
  heroSubtitle: { fontFamily: fonts.body, color: colors.peach, fontSize: '16px', lineHeight: '1.9', marginBottom: '40px' },
  heroCta: { backgroundColor: colors.rose, color: colors.white, textDecoration: 'none', padding: '16px 40px', borderRadius: '2px', fontSize: '14px', fontWeight: '600', fontFamily: fonts.body, letterSpacing: '0.5px', display: 'inline-block' },
  heroFine: { color: 'rgba(255,255,255,0.35)', fontSize: '12px', fontFamily: fonts.body, marginTop: '12px' },
  heroChecklist: { listStyle: 'none', padding: 0, margin: '16px auto 0', display: 'table', textAlign: 'left' },
  heroCheckItem: { color: 'rgba(255,255,255,0.6)', fontSize: '13px', fontFamily: fonts.body, marginBottom: '6px' },
  checkMark: { color: colors.rose, fontWeight: '700', marginRight: '8px' },

  freeResources: { marginTop: '36px', paddingTop: '32px', borderTop: '1px solid rgba(255,255,255,0.12)' },
  freeLabel: { color: 'rgba(255,255,255,0.75)', fontSize: '14px', fontFamily: fonts.body, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '14px', fontWeight: '700' },
  freeBtns: { display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '48px' },
  freeLinkLarge: { backgroundColor: 'transparent', border: `1px solid rgba(255,255,255,0.3)`, color: 'rgba(255,255,255,0.9)', padding: '12px 20px', borderRadius: '2px', fontSize: '15px', fontFamily: fonts.body, fontWeight: '700', letterSpacing: '0.3px', whiteSpace: 'nowrap', textDecoration: 'none', display: 'inline-block' },

  section: { padding: '80px 20px', maxWidth: '1040px', margin: '0 auto' },
  sectionTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '30px', textAlign: 'center', marginBottom: '12px', fontWeight: '600', letterSpacing: '0.3px' },
  sectionSubtitle: { fontFamily: fonts.body, color: colors.rose, fontSize: '12px', textAlign: 'center', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '40px' },

  steps: { display: 'flex', gap: '28px', flexWrap: 'wrap', justifyContent: 'center' },
  step: { flex: '1', minWidth: '240px', maxWidth: '300px', textAlign: 'center', padding: '40px 28px', backgroundColor: colors.offWhite, border: `1px solid ${colors.peach}` },
  stepNum: { fontFamily: fonts.heading, fontSize: '32px', color: colors.rose, marginBottom: '16px', fontWeight: '600' },
  stepTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '16px', marginBottom: '12px', fontWeight: '500' },
  stepDesc: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', lineHeight: '1.8' },

  founderSection: { backgroundColor: colors.navy, padding: '80px 20px' },
  founderInner: { maxWidth: '680px', margin: '0 auto', textAlign: 'center' },
  founderText: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.75)', fontSize: '15px', lineHeight: '2', marginBottom: '20px' },
  founderLink: { marginTop: '12px', backgroundColor: 'transparent', border: `1px solid rgba(255,255,255,0.4)`, color: 'rgba(255,255,255,0.8)', padding: '10px 28px', borderRadius: '2px', fontSize: '13px', fontFamily: fonts.body, letterSpacing: '0.3px', textDecoration: 'none', display: 'inline-block' },

  features: { display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' },
  featureLink: { flex: '1', minWidth: '220px', maxWidth: '260px', padding: '32px 24px', backgroundColor: colors.offWhite, border: `1px solid ${colors.peach}`, borderTop: `3px solid ${colors.rose}`, textDecoration: 'none', display: 'block' },
  featureAccent: { color: colors.gold, fontSize: '18px', marginBottom: '12px' },
  featureTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '15px', marginBottom: '10px', fontWeight: '500' },
  featureTag: { display: 'inline-block', fontFamily: fonts.body, fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: colors.rose, border: `1px solid ${colors.rose}`, padding: '2px 8px', marginBottom: '8px' },
  featureDesc: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', lineHeight: '1.7' },

  ctaSection: { backgroundColor: colors.offWhite, padding: '80px 20px', textAlign: 'center', borderTop: `1px solid ${colors.peach}` },
  ctaLink: { backgroundColor: colors.rose, color: colors.white, textDecoration: 'none', padding: '16px 40px', borderRadius: '2px', fontSize: '14px', fontWeight: '600', fontFamily: fonts.body, letterSpacing: '0.5px', display: 'inline-block' },
  ctaTagline: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px', marginTop: '12px' },

  feedbackSection: { backgroundColor: colors.cream, padding: '80px 20px', borderTop: `1px solid ${colors.peach}` },
  feedbackInner: { maxWidth: '560px', margin: '0 auto', textAlign: 'center' },
  feedbackEyebrow: { fontFamily: fonts.body, color: colors.rose, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '12px' },
  feedbackTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '28px', fontWeight: '600', marginBottom: '12px', letterSpacing: '0.3px' },
  feedbackSubtitle: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '1.8', marginBottom: '32px', fontStyle: 'italic' },
  feedbackForm: { textAlign: 'left' },
  feedbackTextarea: { display: 'block', width: '100%', padding: '12px 16px', fontSize: '14px', fontFamily: fonts.body, border: `1px solid ${colors.peach}`, outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: '12px', color: colors.navy, backgroundColor: colors.white },
  feedbackRow: { display: 'flex', gap: '12px', marginBottom: '12px' },
  feedbackInput: { flex: 1, padding: '10px 14px', fontSize: '13px', fontFamily: fonts.body, border: `1px solid ${colors.peach}`, outline: 'none', color: colors.navy, backgroundColor: colors.white, minWidth: 0 },
  feedbackBtn: { backgroundColor: colors.navy, color: colors.white, border: 'none', padding: '12px 32px', fontSize: '13px', cursor: 'pointer', fontWeight: '600', fontFamily: fonts.body, letterSpacing: '0.5px' },
  feedbackThanks: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '15px', fontStyle: 'italic', marginTop: '16px' },

};
